import { Card } from '../ui/Card';
import { fmt, fmtInt } from '../../lib/format';
import type { CalculatorResults, CalculatorInputs } from '../../lib/types';

interface Props {
  inputs: CalculatorInputs;
  results: CalculatorResults;
}

function dtiColor(val: number, thresholds: [number, number, number]): string {
  if (val <= thresholds[0]) return '#16a34a';
  if (val <= thresholds[1]) return '#d97706';
  if (val <= thresholds[2]) return '#ea580c';
  return '#dc2626';
}

export function AffordabilityCard({ inputs, results }: Props) {
  const a = results.affordability;
  if (!a) return null;

  const frontColor = dtiColor(a.frontDti, [28, 31, 36]);
  const backColor = dtiColor(a.backDti, [36, 43, 50]);

  const barSegments = [
    { label: 'Housing', pct: a.housingPctNet, color: '#2563eb' },
    { label: 'Debts', pct: a.debtPctNet, color: '#7c3aed' },
    { label: 'Living', pct: Math.max(0, a.leftoverPct), color: a.leftoverPct >= 30 ? '#16a34a' : a.leftoverPct >= 15 ? '#d97706' : '#dc2626' },
  ];

  return (
    <Card title="Affordability Analysis">
      {/* Verdict */}
      <div className={`afford-verdict ${a.verdictClass}`}>
        <span className="verdict-icon">{a.verdictIcon}</span>
        <div>
          <div className="verdict-text">{a.verdictText}</div>
          <div className="verdict-sub">{a.verdictSub}</div>
        </div>
      </div>

      {/* DTI Gauges */}
      <div className="gauge-row">
        <div className="gauge-info">
          <div className="gauge-title">
            Front-End DTI <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.7rem' }}>(Housing Only)</span>
          </div>
          <div className="gauge-track">
            <div className="gauge-fill" style={{ width: `${Math.min(a.frontDti, 60) / 60 * 100}%`, background: frontColor }} />
          </div>
          <div className="gauge-markers">
            <div className="gauge-marker" style={{ left: '46.7%' }}>28%</div>
            <div className="gauge-marker" style={{ left: '51.7%' }}>31</div>
            <div className="gauge-marker" style={{ left: '60%' }}>36</div>
          </div>
        </div>
        <div className="gauge-value" style={{ color: frontColor }}>{a.frontDti.toFixed(1)}%</div>
      </div>

      <div className="gauge-row">
        <div className="gauge-info">
          <div className="gauge-title">
            Back-End DTI <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.7rem' }}>(All Debts)</span>
          </div>
          <div className="gauge-track">
            <div className="gauge-fill" style={{ width: `${Math.min(a.backDti, 70) / 70 * 100}%`, background: backColor }} />
          </div>
          <div className="gauge-markers">
            <div className="gauge-marker" style={{ left: '51.4%' }}>36%</div>
            <div className="gauge-marker" style={{ left: '61.4%' }}>43</div>
            <div className="gauge-marker" style={{ left: '71.4%' }}>50</div>
          </div>
        </div>
        <div className="gauge-value" style={{ color: backColor }}>{a.backDti.toFixed(1)}%</div>
      </div>

      {/* Detail Grid */}
      <div className="afford-detail-grid">
        <div className="afford-detail">
          <div className="ad-label">Housing Expense</div>
          <div className="ad-value">${fmt(a.housingExpense)}</div>
        </div>
        <div className="afford-detail">
          <div className="ad-label">Other Debts</div>
          <div className="ad-value">${fmt(a.otherDebts)}</div>
        </div>
        <div className="afford-detail">
          <div className="ad-label">Total Obligations</div>
          <div className="ad-value">${fmt(a.totalObligations)}</div>
        </div>
        <div className="afford-detail">
          <div className="ad-label">Remaining Income</div>
          <div className="ad-value" style={{ color: a.remainingIncome >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {a.remainingIncome >= 0 ? '$' : '-$'}{fmt(Math.abs(a.remainingIncome))}
          </div>
        </div>
      </div>

      {/* Guidelines Table */}
      <div style={{ marginTop: '1rem' }}>
        <div className="gauge-title" style={{ marginBottom: '0.25rem' }}>Lender Guidelines</div>
        <div className="table-scroll-wrap">
          <table className="guideline-table">
            <thead>
              <tr><th>Rule</th><th>Limit</th><th>You</th><th></th></tr>
            </thead>
            <tbody>
              {a.lenderGuidelines.map(r => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td>{r.limit}</td>
                  <td>{r.you}</td>
                  <td className="gl-status" style={{ color: r.pass ? 'var(--green)' : 'var(--red)' }}>
                    {r.pass ? '✓' : '✗'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Net Income Reality Check */}
      {a.netMonthly > 0 && (
        <div className="net-section">
          <div className="net-section-title">Take-Home Pay Reality Check</div>

          <div className="paycheck-bar">
            {barSegments.filter(s => s.pct > 0).map(s => (
              <div
                key={s.label}
                style={{ width: `${Math.min(s.pct, 100).toFixed(1)}%`, background: s.color }}
              >
                {s.pct >= 10 ? s.label : ''}
              </div>
            ))}
          </div>

          <div className="paycheck-legend">
            {barSegments.map(s => (
              <span key={s.label} className="paycheck-legend-item">
                <span className="leg-dot" style={{ background: s.color }} />
                {s.label} <span className="leg-value">{s.pct.toFixed(1)}%</span>
              </span>
            ))}
          </div>

          <div className="net-detail-grid">
            <div className="net-detail">
              <div className="nd-value">${fmt(a.netMonthly)}</div>
              <div className="nd-label">Est. Monthly Take-Home</div>
              <div className="nd-pct" style={{ color: 'var(--text-muted)' }}>{a.taxes.effectiveRate.toFixed(1)}% eff. tax</div>
            </div>
            <div className="net-detail">
              <div className="nd-value" style={{ color: '#2563eb' }}>${fmt(a.housingExpense)}</div>
              <div className="nd-label">Housing Cost</div>
              <div className="nd-pct" style={{ color: '#2563eb' }}>{a.housingPctNet.toFixed(1)}% of take-home</div>
            </div>
            <div className="net-detail">
              <div className="nd-value" style={{ color: a.leftover >= 0 ? 'var(--green)' : 'var(--red)' }}>
                ${fmt(Math.abs(a.leftover))}
              </div>
              <div className="nd-label">{a.leftover >= 0 ? 'Left for Living' : 'Monthly Shortfall'}</div>
              <div className="nd-pct" style={{ color: a.leftoverPct >= 30 ? 'var(--green)' : a.leftoverPct >= 15 ? 'var(--amber)' : 'var(--red)' }}>
                {a.leftoverPct.toFixed(1)}% of take-home
              </div>
            </div>
          </div>

          {/* Daily nudge */}
          <div className={`daily-nudge ${a.nudgeClass}`}>
            <span style={{ fontSize: '1.25rem' }}>{a.nudgeEmoji}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span><span className="nudge-amount">${fmt(Math.max(0, a.dailyLeft))}</span> <span className="nudge-label">/day</span></span>
                <span><span className="nudge-amount">${fmt(Math.max(0, a.weeklyLeft))}</span> <span className="nudge-label">/week</span></span>
                <span style={{ fontSize: '0.75rem' }}>for everything else</span>
              </div>
              <div style={{ fontSize: '0.72rem', marginTop: '0.25rem', opacity: 0.85 }}>{a.nudgeComment}</div>
            </div>
          </div>

          {/* Tax breakdown */}
          <div className="tax-breakdown-mini">
            <span>Federal Income Tax</span><span className="tbm-val">${fmtInt(a.taxes.federal)}/yr</span>
            <span>MN State Income Tax</span><span className="tbm-val">${fmtInt(a.taxes.state)}/yr</span>
            <span>FICA (SS + Medicare)</span><span className="tbm-val">${fmtInt(a.taxes.fica)}/yr</span>
            <span className="tbm-total">Total Tax ({a.taxes.effectiveRate.toFixed(1)}% eff.)</span>
            <span className="tbm-val tbm-total">${fmtInt(a.taxes.totalTax)}/yr</span>
            <span className="tbm-total">Estimated Net Income</span>
            <span className="tbm-val tbm-total">${fmtInt(a.taxes.netAnnual)}/yr → ${fmt(a.netMonthly)}/mo</span>
          </div>

          <div className="note-box" style={{ marginTop: '0.5rem' }}>
            Estimated using 2024 federal brackets, MN state tax, FICA (7.65%), and standard deduction.
            Actual take-home varies with filing status, deductions, 401(k) contributions, and benefits.
          </div>
        </div>
      )}
    </Card>
  );
}
